/**
 * SimilarIssuesSection Component
 *
 * Displays similar resolved issues with similarity scores
 * and expandable resolution playbooks
 */

import React, { useState, useEffect } from 'react';
import {
  SparklesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import enhancedApiService from '../../api/enhancedApiService';
import { useUI } from '../../contexts/UIContext';
import ResolutionPlaybook from './ResolutionPlaybook';

const SimilarIssuesSection = ({ issueId }) => {
  const [similarIssues, setSimilarIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIssueId, setExpandedIssueId] = useState(null);
  const { showNotification } = useUI();

  useEffect(() => {
    if (issueId && issueId !== 'new') {
      loadSimilarIssues();
    }
  }, [issueId]);

  const loadSimilarIssues = async () => {
    try {
      setLoading(true);
      const response = await enhancedApiService({
        method: 'GET',
        url: `/issues/${issueId}/similar`
      });

      if (response.success) {
        setSimilarIssues(response.similar_issues || []);
      }
    } catch (error) {
      console.error('Error loading similar issues:', error);
      // Don't show error notification - it's okay if there are no similar issues
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIssueId(expandedIssueId === id ? null : id);
  };

  const getSimilarityColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800 border-green-200';
    if (score >= 60) return 'bg-blue-100 text-blue-800 border-blue-200';
    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[severity] || colors.medium}`}>
        {severity}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-sm text-gray-600">Finding similar issues...</span>
        </div>
      </div>
    );
  }

  if (similarIssues.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-purple-600" />
          Similar Past Issues
        </h3>
        <div className="text-center py-8 text-gray-500">
          <SparklesIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No similar resolved issues found</p>
          <p className="text-xs mt-1">As issues are resolved, similar patterns will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <SparklesIcon className="h-5 w-5 text-purple-600" />
        Similar Past Issues ({similarIssues.length})
      </h3>

      <div className="space-y-3">
        {similarIssues.map((similar) => (
          <div
            key={similar.id}
            className="border border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 transition-colors"
          >
            {/* Issue Header */}
            <div className="p-4 bg-gray-50">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <a
                      href={`/issues/${similar.id}`}
                      className="text-sm font-medium text-gray-900 hover:text-blue-600"
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = `/issues/${similar.id}`;
                      }}
                    >
                      {similar.title}
                    </a>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span>#{similar.id.split('-')[0]}</span>
                    {getSeverityBadge(similar.severity)}
                    {similar.table_name && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                        Table: {similar.table_name}
                      </span>
                    )}
                  </div>

                  {/* Match Reasons */}
                  {similar.match_reasons && similar.match_reasons.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {similar.match_reasons.map((reason, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-200"
                        >
                          {reason}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Similarity Score Badge */}
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${getSimilarityColor(similar.similarity_score)}`}>
                    {similar.similarity_score}% match
                  </span>

                  {/* Expand/Collapse Button */}
                  <button
                    onClick={() => toggleExpand(similar.id)}
                    className="text-xs text-gray-600 hover:text-blue-600 flex items-center gap-1"
                  >
                    {expandedIssueId === similar.id ? (
                      <>
                        Hide resolution <ChevronUpIcon className="h-4 w-4" />
                      </>
                    ) : (
                      <>
                        Show resolution <ChevronDownIcon className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Resolution Playbook (Expandable) */}
            {expandedIssueId === similar.id && similar.resolution && (
              <ResolutionPlaybook
                resolution={similar.resolution}
                issueId={similar.id}
              />
            )}
          </div>
        ))}
      </div>

      {/* Helper Text */}
      <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
        <p className="text-xs text-purple-800">
          <span className="font-semibold">Tip:</span> These issues were resolved in the past with similar
          patterns. Review their resolutions for helpful approaches to solve your current issue.
        </p>
      </div>
    </div>
  );
};

export default SimilarIssuesSection;
